import store from "@/store";
import router from "@/router";
import { deleteCookie, getCookie } from "@/helpers/cookies";
import type { ISelf } from "@upmind-automation/types";
import _ from "lodash";
import { isValidUrl } from "@/helpers/url";

const win = window as any;

// Set up the global "dataLayer" array
export const UPM_DATA_LAYER = (win["dataLayer"] = win["dataLayer"] || []);

// Set up the global "Universal Event Tracking Queue" array
export const UPM_UETQ = (win["uetq"] = win["uetq"] || []);

// Copy the dataLayer array to the deprecated name for backwards compatibility
win["upmDataLayer"] = win["dataLayer"];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function dataLayer(...args: any[]) {
  // eslint-disable-next-line prefer-rest-params
  UPM_DATA_LAYER.push(arguments);
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function uetq(...args: any[]) {
  // eslint-disable-next-line prefer-rest-params
  UPM_UETQ.push(arguments);
}

/**
 * @name initConsent
 * @description Initialise consent settings for Google Tag Manager and Universal Event Tracking.
 */

const initConsent = () => {
  dataLayer("consent", "default", {
    ad_personalization: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    analytics_storage: "denied",
    functionality_storage: "denied",
    personalization_storage: "denied",
    security_storage: "denied",
    wait_for_update: 500
  });
  uetq("consent", "default", {
    ad_storage: "denied",
    wait_for_update: 500
  });
  dataLayer("set", "ads_data_redaction", true);
  dataLayer("set", "url_passthrough", true);
};

/**
 * @name initGtm
 * @desc Initialise Google Tag Manager with the provided container ID.
 * @param {string} containerId - The Google Tag Manager container ID.
 * @param {string} scriptUrl - The Google Tag Manager script URL.
 */

const initGtm = (containerId: string, scriptUrl?: string) => {
  dataLayer("gtm.start", new Date().getTime());
  dataLayer("event", "gtm.js");

  const src = isValidUrl(scriptUrl)
    ? new URL(scriptUrl as string)
    : new URL("https://www.googletagmanager.com/gtm.js");
  src.searchParams.set("id", containerId);
  src.searchParams.set("l", "dataLayer");

  const first = document.getElementsByTagName("script")[0];
  const script = document.createElement("script");
  script.async = true;
  script.setAttribute("src", src.toString());
  first.parentNode?.insertBefore(script, first);
};

/**
 * @name pushPageUser
 * @desc Push page and user data to the dataLayer (MUST be first).
 */

export const pushPageUser = () => {
  let actor:
    | (Partial<ISelf["analytics"]> & { customer_status: string })
    | null = null;

  try {
    actor = JSON.parse(atob(getCookie("upm_actor") || ""));
  } catch {
    deleteCookie("upm_actor"); // Delete malformed cookie
  }

  const page = _.pickBy(
    {
      page_type: router?.currentRoute?.name || "other",
      environment: import.meta.env.MODE,
      version: import.meta.env.RELEASE,
      language: store.state.i18n.activeLocaleCode.toLocaleUpperCase()
    },
    v => !_.isNil(v)
  );

  const user = _.pickBy(
    {
      clean_email: actor?.clean_email,
      customer_status: actor?.customer_status,
      logged_in: actor?.logged_in ?? false,
      salted_sha_user_id: actor?.salted_sha_user_id,
      sha_user_id: actor?.sha_user_id
    },
    v => !_.isNil(v)
  );

  UPM_DATA_LAYER.push({ page, user });
};

export function install(containerId: string, scriptUrl?: string) {
  initConsent();
  initGtm(containerId, scriptUrl);
  pushPageUser();
}

export default {
  install: (Vue: any) => {
    Object.defineProperty(Vue.prototype, "$dataLayer", {
      value: dataLayer
    });
  }
};
